import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import PropTypes from 'prop-types'
import { checkInputErrorMaxLength, checkInputErrorRequired, useInput, useTextarea } from '@chilecompra/react-kit'
import { Button, Card, CircleSuccessIcon, Dialog, Input, Textarea, Typography } from '@chilecompra/react-kit/components'

import AttachmentsWrapper from '../../../components/customs/AttachmentsWrapper.component'
import {
  CODE_BUSINESS_ATTACHAMENTS_DIRECT_DEAL,
  DIRECT_DEAL_SUMMARY_REQUEST_CONTRACT_STEP_DICTIONARY
} from '../../../config/settings/constants'
import { useImperativeFormRefWithReset } from '../../../modules/hooks/useFormRef'
import { formatDNI } from '../../../modules/utils/formatters'
import { dialogScrollTop } from '../../../modules/utils/dialogScrollTop'
import {
  onCloseContractDialogDirectDealSummary,
  onGetInfoProviderDirectDealSummaryThunk
} from '../DirectDealSummary.actions'
import { AttachmentsContaner, Divider, Grid, Strong } from '../DirectDealSummary.styles'

/**
 * The DirectDealSummaryContract dialog
 */
const DirectDealSummaryContractDialog = ({ contracts, formRef, loading, onSend, provider, userDni }) => {
  const dispatch = useDispatch()

  const [contractValues, setContractValues] = useState(null)
  const [openModalAttachRequest, setOpenModalAttachRequest] = useState(false)
  const [openModalDeleteAllRequest, setOpenModalDeleteAllRequest] = useState(false)

  const successMessageRef = useRef(null)

  const {
    error: answerRequestError,
    onChange: answerRequestChange,
    onError: onAnswerRequestError,
    onReset: onAnswerRequestReset,
    setValue: setAnswerRequestValue,
    value: answerRequestValue
  } = useTextarea({
    errorCallbacks: [checkInputErrorMaxLength(500), checkInputErrorRequired()],
    initialValue: contractValues?.answer || ''
  })

  const {
    error: dniRequestError,
    onChange: onDniRequestChange,
    onError: onDniRequestError,
    value: dniRequestValue
  } = useInput({
    changeCallback: updatedValue => {
      return updatedValue?.length > 10 && dispatch(onGetInfoProviderDirectDealSummaryThunk({ dni: updatedValue }))
    },
    errorCallbacks: [checkInputErrorRequired()],
    formatCallbacks: [value => formatDNI(value)],
    initialValue: userDni || ''
  })

  const {
    error: businessRequestError,
    onChange: businessRequestChange,
    onError: onBusinessRequestError,
    setValue: setBusinessRequestValue,
    value: businessRequestValue
  } = useInput({
    errorCallbacks: [checkInputErrorRequired()],
    initialValue: provider?.providerLegalName || ''
  })

  const disabledSend = useMemo(() => {
    const hasEmpty = answerRequestValue === '' || businessRequestValue === '' || dniRequestValue === ''
    const hasError = answerRequestError !== '' || businessRequestError !== '' || dniRequestError !== ''

    return hasEmpty || hasError
  }, [
    answerRequestError,
    answerRequestValue,
    businessRequestError,
    businessRequestValue,
    dniRequestError,
    dniRequestValue
  ])

  const handlerCloseRequest = () => {
    dispatch(onCloseContractDialogDirectDealSummary())
    onAnswerRequestReset()
    setAnswerRequestValue('')
  }

  useEffect(() => {
    if (provider && provider?.providerLegalName) {
      setBusinessRequestValue(provider.providerLegalName)
    } else {
      setBusinessRequestValue('')
    }
  }, [provider])

  useImperativeFormRefWithReset(
    formRef,
    () => ({
      answerRequestValue: {
        error: answerRequestError,
        onChange: answerRequestChange,
        onError: onAnswerRequestError,
        value: answerRequestValue
      },
      businessRequestError,
      businessRequestValue,
      dniRequestError,
      dniRequestValue
    }),
    [answerRequestValue, businessRequestError, businessRequestValue, dniRequestError, dniRequestValue]
  )

  useEffect(() => {
    setContractValues(contracts.requests.find(request => request.id === contracts?.sheetIdRequesting))
  }, [contracts?.openForm])

  useEffect(() => {
    if (contracts?.step === DIRECT_DEAL_SUMMARY_REQUEST_CONTRACT_STEP_DICTIONARY.SUCCESS && successMessageRef.current) {
      successMessageRef.current.focus()
      dialogScrollTop()
    }
  }, [contracts?.step])

  return (
    <Dialog
      disableBackdropClick
      maxWidth={contracts?.step === DIRECT_DEAL_SUMMARY_REQUEST_CONTRACT_STEP_DICTIONARY.SUCCESS ? '500px' : '800px'}
      onClose={handlerCloseRequest}
      open={contracts?.openForm || false}
      padding="16px"
      verticalAlign="top"
    >
      {contracts.step === DIRECT_DEAL_SUMMARY_REQUEST_CONTRACT_STEP_DICTIONARY.FORM && (
        <Grid container padding="16px">
          <Grid container direction="column" gap="8px" item xs={12}>
            <Typography color="black1" fontWeight="bold" variant="h3">
              Solicitud de un nuevo proceso de contratación
            </Typography>

            <Typography color="black1" variant="body2">
              Si ofreces el mismo producto o servicio, o conoces otro proveedor que lo haga por favor adjunta la
              documentación necesaria que lo demuestre.
            </Typography>
          </Grid>

          <Divider margin="24px 0" />

          <Card color="primary" margin="0 0 16px">
            <Typography color="black1" variant="body2">
              De acuerdo a la causal señalada en el Art. 8 bis de la ley 19.886, si ofreces los mismos productos o
              servicios solicitados por el organismo comprador en este Trato Directo, tendrás la posibilidad de
              responder y solicitar que se realice otro proceso de contratación, la respuesta que ingreses será de
              <Strong> carácter público</Strong> y todos los usuarios podrán visualizarla identificando la fuente.
            </Typography>
          </Card>

          <Grid container item spacing={2} xs={12}>
            <Grid item xs={12}>
              <Textarea
                error={answerRequestError}
                label="Ingresa tu respuesta"
                loading={loading}
                maxCount={500}
                onChange={answerRequestChange}
                onError={onAnswerRequestError}
                value={answerRequestValue}
              />
            </Grid>

            <Grid item sm={6} xs={12}>
              <Input
                disabled
                error={dniRequestError}
                label="RUT"
                loading={loading}
                onChange={onDniRequestChange}
                onError={onDniRequestError}
                value={dniRequestValue}
              />
            </Grid>

            <Grid item sm={6} xs={12}>
              <Input
                disabled
                error={businessRequestError}
                label="Razón social"
                loading={loading}
                onChange={businessRequestChange}
                onError={onBusinessRequestError}
                value={businessRequestValue}
              />
            </Grid>
          </Grid>

          <Grid container direction="column" gap="8px" item xs={12}>
            <Typography fontWeight="bold" variant="body2">
              Adjuntar evidencia
            </Typography>

            <Typography variant="subtitle">
              Adjunta documentos que demuestren la veracidad de tu respuesta. Se admiten archivos con un peso máximo de
              20mb.
            </Typography>

            <AttachmentsContaner>
              <AttachmentsWrapper
                business={CODE_BUSINESS_ATTACHAMENTS_DIRECT_DEAL}
                canDelete
                canRead
                canWrite
                canWriteBulk
                color="blank"
                idBusiness={contractValues?.idBusiness}
                isPrivate
                openModal={openModalAttachRequest}
                openModalDeleteAll={openModalDeleteAllRequest}
                setOpenModal={setOpenModalAttachRequest}
                setOpenModalDeleteAll={setOpenModalDeleteAllRequest}
                subBusiness={contractValues?.subBusiness}
              />
            </AttachmentsContaner>
          </Grid>

          <Grid alignItems="center" container item gap="32px" justifyContent="center" xs={12}>
            <Button aria-label="Cerrar" onClick={handlerCloseRequest} variant="contained">
              Cerrar
            </Button>

            <Button
              aria-label="Enviar solicitud"
              color="primary"
              disabled={disabledSend}
              loading={loading}
              onClick={onSend}
              variant="contained"
            >
              Enviar solicitud
            </Button>
          </Grid>
        </Grid>
      )}

      {contracts.step === DIRECT_DEAL_SUMMARY_REQUEST_CONTRACT_STEP_DICTIONARY.SUCCESS && (
        <Grid
          alignItems="center"
          container
          direction="column"
          gap="16px"
          justifyContent="center"
          padding="16px"
          style={{ textAlign: 'center' }}
        >
          <CircleSuccessIcon />

          <Typography ref={successMessageRef} color="black1" fontWeight="bold" variant="h3">
            Solicitud enviada con éxito
          </Typography>

          <Typography color="black1" variant="body2">
            La solicitud de un nuevo proceso de contratación fue enviada y será evaluada por el organismo comprador.
          </Typography>

          <Button
            aria-label="Cerrar"
            color="success"
            margin="16px 0 0"
            onClick={handlerCloseRequest}
            variant="contained"
          >
            Cerrar
          </Button>
        </Grid>
      )}
    </Dialog>
  )
}

DirectDealSummaryContractDialog.propTypes = {
  contracts: PropTypes.shape({
    requests: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        dni: PropTypes.string,
        isActive: PropTypes.bool
      })
    ),
    openForm: PropTypes.bool,
    step: PropTypes.string,
    sheetIdRequesting: PropTypes.string
  }),
  formRef: PropTypes.shape({
    current: PropTypes.shape(Object)
  }),
  loading: PropTypes.bool,
  onSend: PropTypes.func,
  provider: PropTypes.shape({
    providerLegalName: PropTypes.string,
    providerDni: PropTypes.string
  }),
  userDni: PropTypes.string
}

export default DirectDealSummaryContractDialog
